import { create } from 'zustand';
import type { InventoryMovementDto, LowStockDto, StockTransferRequest, VariantStockByLocationDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { LOW_STOCK_LIMIT, type RecountReason } from '@/lib/stock';

/**
 * Inventory.
 *
 * A recount takes the **counted figure**, not a delta — an operator counts what
 * is on the shelf, and asking them to work out the difference is how a stock
 * correction becomes a stock error. The API records the signed movement, with
 * the reason the operator gives.
 *
 * The list opens on the store's own low-stock threshold — the one the
 * overview counts against — by sending none; a number is an explicit view.
 */

interface InventoryState {
  lowStock: LowStockDto[];
  /** Null: the store's own setting, which the API applies when none is sent. */
  threshold: number | null;
  /** SKU or product name, either language. */
  search: string;
  categoryId: string;
  loading: boolean;
  error: string | null;

  /** Whose history is open — kept whole, so it stays named after the list moves on. */
  movementsOf: LowStockDto | null;
  movements: InventoryMovementDto[];
  movementsCursor: string | null;
  movementsLoading: boolean;
  movementsLoadingMore: boolean;
  movementsError: string | null;

  /** What each location holds of the open variant (ADR-089) — one row when the store has one place. */
  locations: VariantStockByLocationDto | null;

  saving: boolean;
  saveError: string | null;

  load: (patch?: { threshold?: number | null; search?: string; categoryId?: string }) => Promise<void>;
  loadMovements: (item: LowStockDto) => Promise<void>;
  loadMoreMovements: () => Promise<void>;
  recount: (item: LowStockDto, input: { stock: number; reason: RecountReason; note?: string; locationId?: string }) => Promise<boolean>;
  loadLocations: (variantId: string) => Promise<void>;
  transfer: (item: LowStockDto, input: StockTransferRequest) => Promise<boolean>;
  clearSaveError: () => void;
}

/** A page of history at a time — the API's default page, and the platform's. */
const MOVEMENTS_PAGE = 20;

// Only the latest request may write: a fast typist's earlier search, or the
// history of a row clicked a moment before, must not land over the newer one.
let listRequest = 0;
let movementsRequest = 0;

export const useInventoryStore = create<InventoryState>((set, get) => ({
  lowStock: [],
  threshold: null,
  search: '',
  categoryId: '',
  loading: false,
  error: null,

  movementsOf: null,
  movements: [],
  movementsCursor: null,
  movementsLoading: false,
  movementsLoadingMore: false,
  movementsError: null,

  locations: null,

  saving: false,
  saveError: null,

  async load(patch) {
    const state = get();
    const next = {
      threshold: patch && 'threshold' in patch ? (patch.threshold ?? null) : state.threshold,
      search: patch?.search ?? state.search,
      categoryId: patch?.categoryId ?? state.categoryId,
    };
    const request = ++listRequest;
    set({ loading: true, error: null, ...next });

    try {
      const page = await withSession(() =>
        adminApi.inventory.lowStock({
          threshold: next.threshold ?? undefined,
          search: next.search || undefined,
          categoryId: next.categoryId || undefined,
          limit: LOW_STOCK_LIMIT,
        }),
      );
      if (request !== listRequest) return;
      set({ lowStock: page.items, loading: false });
    } catch (error) {
      if (request !== listRequest) return;
      set({ loading: false, error: errorMessage(error, 'Could not load stock levels.') });
    }
  },

  async loadMovements(item) {
    const request = ++movementsRequest;
    set({
      movementsOf: item,
      movements: [],
      movementsCursor: null,
      movementsLoading: true,
      movementsLoadingMore: false,
      movementsError: null,
    });
    void get().loadLocations(item.variantId);

    try {
      const page = await withSession(() => adminApi.inventory.movements(item.variantId, { limit: MOVEMENTS_PAGE }));
      if (request !== movementsRequest) return;
      set({ movements: page.items, movementsCursor: page.meta.nextCursor, movementsLoading: false });
    } catch (error) {
      if (request !== movementsRequest) return;
      set({ movementsLoading: false, movementsError: errorMessage(error, 'Could not load stock history.') });
    }
  },

  async loadMoreMovements() {
    const { movementsOf, movementsCursor, movementsLoadingMore } = get();
    if (!movementsOf || !movementsCursor || movementsLoadingMore) return;

    const request = movementsRequest;
    set({ movementsLoadingMore: true, movementsError: null });

    try {
      const page = await withSession(() =>
        adminApi.inventory.movements(movementsOf.variantId, { limit: MOVEMENTS_PAGE, cursor: movementsCursor }),
      );
      if (request !== movementsRequest) return;
      set((state) => ({
        movements: [...state.movements, ...page.items],
        movementsCursor: page.meta.nextCursor,
        movementsLoadingMore: false,
      }));
    } catch (error) {
      if (request !== movementsRequest) return;
      set({ movementsLoadingMore: false, movementsError: errorMessage(error, 'Could not load more history.') });
    }
  },

  async recount(item, input) {
    set({ saving: true, saveError: null });

    try {
      await withSession(() =>
        adminApi.inventory.recount(item.variantId, {
          stock: input.stock,
          reason: input.reason,
          note: input.note,
          locationId: input.locationId,
        }),
      );

      set({ saving: false });
      // The list may lose the row — counted above the threshold — but its open
      // history stays, now ending in the movement just recorded.
      void get().load();
      if (get().movementsOf?.variantId === item.variantId) {
        // At a location the figure was that place's; the total is re-read with the rest.
        void get().loadMovements(input.locationId ? item : { ...item, stock: input.stock });
      } else {
        void get().loadLocations(item.variantId);
      }
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the recount.') });
      return false;
    }
  },

  async loadLocations(variantId) {
    try {
      const locations = await withSession(() => adminApi.inventory.byLocation(variantId));
      set({ locations });
    } catch {
      // Where the stock is, beside the history: a failed read hides it rather than blocking the screen.
      set({ locations: null });
    }
  },

  async transfer(item, input) {
    set({ saving: true, saveError: null });
    try {
      const locations = await withSession(() => adminApi.inventory.transfer(item.variantId, input));
      set({ saving: false, locations });
      if (get().movementsOf?.variantId === item.variantId) void get().loadMovements(item);
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not move the stock.') });
      return false;
    }
  },

  clearSaveError() {
    set({ saveError: null });
  },
}));
